<script lang="ts">
	import { onMount, onDestroy } from 'svelte'
	import type { ChatMessage, InSessionData, MessageActionType } from '@repo/commons/models'
	import {
		Button,
		ButtonSize,
		ButtonType,
		DynamicForm,
		Icon,
		Label
	} from '@repo/commons/components'
	import {
		MessageFooter,
		MessageSenderAvatar,
		FormMessage,
		AgentView,
		TemplateRenderer,
		DocumentReference,
		MessageActions
	} from './index.ts'
	import type { RetrievedDocument } from 'chatbridge-client/src'
	import {
		createTypingAnimation,
		copyToClipboard,
		type TypingAnimationController
	} from '@repo/commons/utils'
	import type { IconName } from '$lib_commons/svg-icons/iconsMap.ts'
	import { useSetting, SETTING_KEYS } from '@repo/commons/settings'

	interface Props {
		message: ChatMessage
		showAvatar?: boolean
		allowMessageEditing?: boolean
		showFeedbackButtons?: boolean
		showMessageActions?: boolean
		enableTypingAnimation?: boolean // Enable typing animation for assistant messages
		onSessionDataClick?: (inSessionData: InSessionData | undefined) => void
		onButtonClick?:
			| (({ interactionId, buttonId }: { interactionId: string; buttonId: string }) => void)
			| null
		onFormSubmit?: (formData: Record<string, any>) => void
		onActionClick?: ((action: MessageActionType, interactionId: string) => void) | null
		onCustomComponentClicked:
			| ((id: string, componentType: string, props: Record<string, any>) => void)
			| null
		onDocumentClick?: ((doc: RetrievedDocument) => void) | null
		onImageLoad?: ((imageFileName: string) => Promise<Blob | string>) | null
		onOpenImageModal?: ((imageUrl: string) => void) | null
		processImages?: boolean
	}

	let {
		message,
		showAvatar = true,
		allowMessageEditing = true,
		showFeedbackButtons = true,
		showMessageActions = true,
		enableTypingAnimation = false,
		onButtonClick = null,
		onFormSubmit,
		onSessionDataClick,
		onActionClick = null,
		onCustomComponentClicked = null,
		onDocumentClick = null,
		onImageLoad = null,
		onOpenImageModal = null,
		processImages = false
	}: Props = $props()

	const { showSessionActivity } = useSetting({
		showSessionActivity: SETTING_KEYS.SHOW_SESSION_ACTIVITY
	})

	let { role, timestamp, systemInteractionMetadata } = message
	let duration = systemInteractionMetadata?.duration || 0

	// Typing animation state
	let displayedUtterance = $state(message.utterance || '')
	let isTyping = $state(false)
	let typingController: TypingAnimationController | null = null

	function onActionCallback(interactionId: string, buttonId: string) {
		onButtonClick?.({ interactionId, buttonId })
	}

	// Initialize typing animation for all streamed messages
	onMount(() => {
		if (enableTypingAnimation) {
			typingController = createTypingAnimation({
				text: message.utterance || '',
				typingSpeed: 15,
				onUpdate: (text) => {
					displayedUtterance = text
					isTyping = true
				},
				onComplete: () => {
					isTyping = false
				}
			})
			typingController.start()
		} else {
			displayedUtterance = message.utterance || ''
		}
	})

	$effect(() => {
		if (typingController) {
			typingController.updateText(message.utterance || '')
		}
	})

	onDestroy(() => {
		typingController?.cleanup()
	})

	const direction = $derived.by(() => {
		if (role === 'ASSISTANT' || role === 'SYSTEM') {
			return 'align-left'
		} else if (role === 'USER') {
			return 'align-right'
		} else {
			return 'align-center'
		}
	})

	const formClassName = $derived.by(() => {
		return message.displayButtons?.length ? 'buttons' : 'forms'
	})

	const wrapperClassName = $derived.by(
		() => `message-wrapper ${direction} ${allowMessageEditing ? 'editing' : ''}`
	)

	// Group PDF documents by ID (same PDF, different pages) and collect their pages
	const groupedDocuments = $derived.by(() => {
		if (!message.retrievedDocuments) return null

		const pdfGroups = new Map<string, { doc: RetrievedDocument; pages: number[] }>()
		const nonPdfDocs: RetrievedDocument[] = []

		message.retrievedDocuments.forEach((doc) => {
			const metadata = doc.knowledgeResourceMetadata
			if (metadata?.type === 'PDF') {
				// Group by metadata.id (same knowledge resource)
				const resourceId = metadata.id || doc.id || 'unknown'
				const pageIndex = metadata.pageIndex ?? 0

				if (pdfGroups.has(resourceId)) {
					const group = pdfGroups.get(resourceId)!
					if (!group.pages.includes(pageIndex)) {
						group.pages.push(pageIndex)
					}
				} else {
					pdfGroups.set(resourceId, { doc, pages: [pageIndex] })
				}
			} else {
				nonPdfDocs.push(doc)
			}
		})

		// Sort pages within each group
		pdfGroups.forEach((group) => {
			group.pages.sort((a, b) => a - b)
		})

		return [...Array.from(pdfGroups.values()), ...nonPdfDocs.map((doc) => ({ doc, pages: [] }))]
	})

	const getFileIcon = (fileType: string): IconName => {
		if (fileType.startsWith('image')) {
			return 'image'
		} else if (fileType.endsWith('text/csv')) {
			return 'fileCsv'
		} else if (fileType.startsWith('application/pdf')) {
			return 'filePdf'
		}
		return 'file'
	}
</script>

{#if showSessionActivity.value && message.systemInteractionMetadata?.sessionViewerBaseEvents?.length}
	<div class="session-events-container">
		<div class="session-events-header">
			<div class="session-events-divider"></div>
			<span class="session-events-label">
				<Icon name="activityStar" size="sm" />
				Session Activity
			</span>
			<div class="session-events-divider"></div>
		</div>
		<div class="session-events-list">
			{#each message.systemInteractionMetadata.sessionViewerBaseEvents as activity, i (i)}
				<Label label={activity} size="sm" variant="favorite" multiline />
			{/each}
		</div>
	</div>
{/if}

<div class={wrapperClassName}>
	{#if role === 'USER' && allowMessageEditing}
		<div class="edit-container">
			<div class="edit-action">
				<Button
					buttonType={ButtonType.CUSTOM}
					onclick={() => onActionClick?.('EDIT', message.interactionId ?? '')}
				>
					<Icon name="edit" class="text-[#002C7D80]" size="md" />
				</Button>
			</div>
		</div>
	{/if}
	{#if message}
		<div class="message-item-wrapper">
			<div class={`message-item ${direction}`}>
				<div class="text-content {role}">
					<p class="message-text text-base">
						{#if message.isForm}
							<FormMessage data={message.utterance} />
						{:else}
							<TemplateRenderer
								utterance={displayedUtterance}
								utterancePayload={message.utterancePayload}
								onComponentClick={onCustomComponentClicked || null}
								{onImageLoad}
								{onOpenImageModal}
								{processImages}
							/>
							<!-- {#if isTyping}
								<span class="typing-cursor">▊</span>
							{/if} -->
						{/if}
					</p>

					{#if role === 'ASSISTANT' && message?.systemInteractionMetadata?.inSessionData}
						<div>
							<div class="in-session-data-separator"></div>
							<!-- <p class="in-session-data-title">In-Session Data:</p> -->
							<!-- <ul class="in-session-data-wrapper">
								<li>
									<span class="text-base">
										intent={message?.systemInteractionMetadata?.inSessionData.intent ?? 'Not set'}
									</span>
								</li>
								<li>
									<span class="text-base">
										amount={message?.systemInteractionMetadata?.inSessionData.amount ?? 'Not set'}
									</span>
								</li>
								<li>
									<span class="text-base">
										type={message?.systemInteractionMetadata?.inSessionData.type ?? 'Not set'}
									</span>
								</li>
							</ul> -->
							<Button
								buttonText="In-Session Data"
								buttonType={ButtonType.LINK}
								buttonSize={ButtonSize.SMALL}
								onclick={() =>
									onSessionDataClick?.(message?.systemInteractionMetadata?.inSessionData)}
							/>
						</div>
					{/if}

					{#if message?.attachments?.length}
						<div class="in-session-data-separator"></div>
						<div class="message-attachments">
							<h6>Attachments:</h6>
							<div class="message-attachment-list">
								{#each message.attachments as attachment}
									<div class="message-attachment">
										<div class="icon-wrapper">
											<Icon
												name={getFileIcon(attachment.fileType)}
												size="sm"
												class="text-[#002C4D80]"
											/>
										</div>
										<p>{attachment.fileName}</p>
									</div>
								{/each}
							</div>
						</div>
					{/if}

					{#if message.retrievedDocuments && message.retrievedDocuments.length > 0}
						<div class="retrieved-documents">
							<div class="documents-divider"></div>
							<h4 class="documents-title">References</h4>
							{#if message.isFiltered}
								<div class="documents-list">
									{#each groupedDocuments as group, index (group.doc.id + '-' + index)}
										<DocumentReference doc={group.doc} pages={group.pages} {onDocumentClick} />
									{/each}
								</div>
							{:else if groupedDocuments !== null}
								<div class="documents-loading">Loading sources...</div>
							{/if}
						</div>
					{/if}

					{#if timestamp || duration}
						<MessageFooter {role} {timestamp} {duration} />
					{/if}
					{#if showMessageActions}
						{#if role === 'ASSISTANT'}
							<MessageActions
								onclick={(e) => {
									if (e === 'COPY') {
										copyToClipboard(message.utterance || '')
									}
									onActionClick?.(e, message.interactionId ?? '')
								}}
								{showFeedbackButtons}
							/>
						{:else if role === 'USER'}
							<MessageActions
								onclick={(e) => {
									if (e === 'COPY') {
										copyToClipboard(message.utterance || '')
									}
									onActionClick?.(e, message.interactionId ?? '')
								}}
								showFeedbackButtons={false}
							/>
						{/if}
					{/if}
				</div>
			</div>

			{#if (message.displayButtons?.length || message.formFields?.fields?.length) && !message.responded}
				<div class={`message-item ${formClassName}`}>
					{#if !!message.displayButtons?.length}
						<div class="actionsWrapper">
							{#each message.displayButtons as opt}
								<Button
									buttonType={ButtonType.NEUTRAL}
									onclick={() => onActionCallback(message.interactionId ?? '', opt.transitionId)}
									buttonText={opt.buttonLabel}
								/>
							{/each}
						</div>
					{/if}
					{#if !!message.formFields?.fields.length}
						<div class="actionsWrapper">
							<DynamicForm
								formClassName="dinamicForm"
								schema={{ fields: message.formFields?.fields }}
								onSubmit={onFormSubmit!}
							/>
						</div>
					{/if}
				</div>
			{/if}
		</div>
	{/if}
	{#if showAvatar}
		<MessageSenderAvatar {role} />
	{/if}
</div>

{#if message.callcenterAgentHandoverPayload}
	<div class="message-item agent-view">
		<AgentView {role} />
	</div>
{/if}

<style>
	.message-wrapper {
		@apply flex flex-row items-end gap-2 w-full;
	}

	.message-wrapper.editing {
		/* @apply !items-center gap-0; */
	}

	.message-wrapper.align-left {
		@apply justify-end flex-row-reverse;
	}

	.message-wrapper.align-right {
		@apply justify-end;
	}

	.message-wrapper.align-center {
		@apply justify-center;
	}

	.message-item-wrapper {
		width: min(544px, calc(50% - 96px));
	}

	.message-item {
		@apply flex flex-col items-start gap-6 self-stretch w-full px-6 pt-6 pb-3;
	}

	.actionsWrapper {
		@apply flex gap-2 flex-wrap w-full;
	}

	.message-item.align-left {
		@apply rounded-[24px_24px_24px_0px] bg-[rgba(0,73,128,0.06)];
	}

	.message-item.align-right {
		@apply rounded-[24px_24px_0px_24px] bg-[rgba(20,213,134,0.2)];
	}

	.message-item.align-center {
		@apply flex flex-col items-center gap-[10px] self-stretch py-6 px-0;
	}

	.message-item.agent-view {
		@apply items-center rounded-none bg-none;
	}

	.message-item.align-left .text-content,
	.message-item.align-right .text-content,
	.message-item.align-center .text-content {
		@apply text-[16px] font-normal leading-6 w-full;
	}

	.message-item.align-right .text-content {
		@apply text-[rgba(0,22,38,0.9)];
	}

	.message-item.align-left .text-content {
		@apply text-[#1e1e1e];
	}

	.message-item.align-center .text-content {
		@apply text-[rgba(0,29,51,0.7)];
	}

	.message-item.forms {
		@apply rounded-[24px_24px_24px_24px] mt-4 border border-[#003a661a] bg-[rgba(0,73,128,0.06)];
	}

	.message-item.buttons {
		@apply !pl-0 !pr-0;
	}

	.message-item.buttons .actionsWrapper {
		@apply justify-end;
	}

	.text-content.system {
		@apply flex gap-[5px];
	}

	.see-all {
		@apply text-[#007acc] text-[14px] font-normal leading-5 underline;
	}

	.cover {
		@apply w-12 h-12 rounded-full object-fill;
	}

	.session-events-container {
		@apply w-full py-4 px-2;
	}

	.session-events-header {
		@apply flex items-center gap-3 mb-3;
	}

	.session-events-divider {
		@apply flex-1 border-t border-[#003A661A];
	}

	.session-events-label {
		@apply flex items-center gap-1.5 text-xs font-medium whitespace-nowrap text-[#7A8A99];
	}

	.session-events-list {
		@apply flex flex-wrap items-center justify-center gap-2;
	}

	.in-session-data-title {
		@apply text-base font-semibold mb-2;
	}

	p.message-text {
		margin: 0;
		width: 100%;
	}

	p.message-text :global(a) {
		@apply text-[#1A74A8] underline;
	}

	.in-session-data-separator {
		@apply border-b-[#003A661A] border-b mt-4 mb-4;
	}

	.in-session-data-wrapper {
		@apply flex flex-col gap-[2px] mb-2;
	}

	.retrieved-documents {
		@apply mt-3 gap-2 flex flex-col;
	}

	.documents-divider {
		@apply border-t-2 border-dotted border-gray-300 my-2;
	}

	.documents-title {
		@apply font-semibold text-sm text-gray-800 mb-2;
	}

	.documents-loading {
		@apply text-sm text-gray-500 italic;
	}

	.documents-list {
		@apply space-y-0 gap-2 flex flex-col;
	}

	.edit-container {
		@apply min-w-[40px] min-h-[40px] flex items-center justify-center;
	}

	.edit-container .edit-action {
		display: none;
	}

	.edit-container:hover .edit-action {
		display: block;
	}

	.message-wrapper:hover .edit-container .edit-action {
		display: block;
	}

	.message-attachments {
		@apply py-1;

		h6 {
			@apply text-base font-semibold text-gray-800 mb-2;
		}
	}

	.message-attachments p {
		@apply text-sm ml-1 truncate;
	}

	.message-attachment-list {
		@apply flex flex-col gap-2;
	}

	.message-attachment {
		@apply flex items-center bg-[#00498011] rounded-md px-1 py-1.5;

		.icon-wrapper {
			@apply flex items-center justify-center w-8;
		}
	}

	:global(.calendar-wrapper) {
		@apply !z-[99];
	}

	:global(.calendar-wrapper .calendar) {
		@apply w-full;
	}

	:global(.actionsWrapper .dinamicForm) {
		@apply flex flex-col gap-4 w-full;
	}

	/* Optional: Show cursor pointer when typing (user can click to skip) */
	.text-content {
		cursor: default;
	}

	/* Blinking cursor for typing animation */
	.typing-cursor {
		display: none;
		margin-left: 2px;
		animation: blink 1s step-end infinite;
		color: currentColor;
	}

	@keyframes blink {
		0%,
		100% {
			opacity: 1;
		}
		50% {
			opacity: 0;
		}
	}
</style>
